import React, { useState } from 'react';
import { useRouter } from 'next/router';
import {
  EmptyState,
  EmptyStateText,
  EmptyStateTitle,
  IconCheckAltFill,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { HeldReleasesFilters } from '@lib/api/artistWatchlist/types';
import { Resource } from '@hooks/useCurrentUserInfo/types';
import { useHeldReleases } from '@hooks/useHeldReleases';
import { usePagination } from '@hooks/usePagination';
import { CopyReleaseId } from '@components/shared/CopyReleaseId';
import { DataTable } from '@components/shared/DataTable';
import { Loading } from '@components/shared/Loading';
import { NavigationLink } from '@components/shared/ResultsTable/NavigationLink';
import { RetryableLoadingError } from '@components/shared/RetryableLoadingError';
import { SORT_DIRECTION } from '@helpers/constants';
import { dateFromProtoTimestamp } from '@helpers/utils';
import { AlbumMetadataListResponse } from '@generated/grpc/spotify/artistwatchlist/v1';
import { ErrorType } from '@generated/grpc/spotify/artistwatchlist/v1/ErrorType';
import { ActionTableCell } from './HeldReleasesTable/ActionTableCell';
import { DateSubmittedCell } from './HeldReleasesTable/DateSubmittedCell';
import { ReasonTableCell } from './HeldReleasesTable/ReasonTableCell';
interface HeldReleasesProps {
  userLicensors: Resource[];
  filter: HeldReleasesFilters;
}

export const HeldReleases: React.VFC<HeldReleasesProps> = ({
  userLicensors,
  filter,
}) => {
  const t = useT();
  const [insertedByDateSortDirection, setInsertedByDateSortDirection] =
    useState<SORT_DIRECTION>(SORT_DIRECTION.descending);
  const { limit, page, renderPagination } = usePagination();
  const { data, ok, isLoading, mutate, hasMore } = useHeldReleases(
    userLicensors.map(licensor => licensor.id),
    page,
    limit,
    insertedByDateSortDirection === SORT_DIRECTION.ascending,
    filter,
  );
  const router = useRouter();
  const retryOrRedirect = () => {
    if (data?.error?.type === ErrorType.NOT_AUTHORIZED) {
      router.push({ pathname: router.pathname });
    } else {
      mutate();
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!ok || data?.error) {
    return <RetryableLoadingError onRetry={retryOrRedirect} />;
  }

  if (
    ok &&
    page === 1 &&
    data?.albumMetadataListResponse?.albumMetadata?.length === 0
  ) {
    return (
      <EmptyState icon={IconCheckAltFill} variant="contextual">
        <EmptyStateTitle>
          {t(
            'atlas.held_releases.empty_state_title',
            'No products are held in review.',
            'Title for empty state when users have zero on held releases in the table',
          )}
        </EmptyStateTitle>
        <EmptyStateText>
          {t(
            'atlas.held_releases.empty_state_description',
            'Nice work!',
            'Description when users have zero on held releases in the table',
          )}
        </EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <>
      <DataTable
        headers={[
          {
            width: '15%',
            property: 'licensorName',
            label: t(
              'atlas.labels.licensor_name',
              'Licensor name',
              `Label for Licensor name`,
            ),
          },
          {
            width: '20%',
            property: 'albumName',
            label: t(
              'atlas.labels.product_details',
              'Product details',
              `Label for Product details`,
            ),
            renderRow: row => (
              <NavigationLink albumUri={row.albumUri!}>
                <Type variant="viola" weight="bold" semanticColor="textBase">
                  <div>
                    <Type variant="viola" semanticColor="textBase">
                      {row.albumName}
                    </Type>
                  </div>
                  <Type variant="viola" semanticColor="textSubdued">
                    {row.artists?.map(artist => artist.artistName).join(', ') ||
                      ''}
                  </Type>
                </Type>
              </NavigationLink>
            ),
          },
          {
            width: '15%',
            property: 'primaryReleaseId',
            label: t(
              'atlas.labels.primary_release_id',
              'Primary release ID',
              'Label for Primary release ID',
            ),
            renderRow: row =>
              row.primaryReleaseId ? (
                <Type variant="viola" semanticColor="textBase">
                  <CopyReleaseId primaryReleaseId={row.primaryReleaseId} />
                </Type>
              ) : (
                <NavigationLink albumUri={row.albumUri!}>
                  <Type variant="viola" semanticColor="textBase">
                    {' '}
                    -{' '}
                  </Type>
                </NavigationLink>
              ),
          },
          {
            width: '15%',
            property: 'insertedDate',
            isSortable: true,
            sortedByDirection: insertedByDateSortDirection,
            onClickSort: () =>
              setInsertedByDateSortDirection(
                insertedByDateSortDirection === SORT_DIRECTION.ascending
                  ? SORT_DIRECTION.descending
                  : SORT_DIRECTION.ascending,
              ),
            label: t(
              'atlas.labels.insert_date',
              'Insert date (UTC)',
              'Label for Insert Date',
            ),
            renderRow: row => {
              const insertedDateAndTime = dateFromProtoTimestamp(
                row.insertedDate,
              );

              return (
                <NavigationLink albumUri={row.albumUri!}>
                  <Type variant="viola" weight="bold" semanticColor="textBase">
                    <div>
                      <Type variant="viola" semanticColor="textBase">
                        {insertedDateAndTime.date}
                      </Type>
                    </div>
                    <Type variant="viola" semanticColor="textSubdued">
                      {insertedDateAndTime.time}
                    </Type>
                  </Type>
                </NavigationLink>
              );
            },
          },
          {
            width: '15%',
            property: 'blockedReason',
            label: t(
              'atlas.labels.reason',
              'Reason',
              'Label for Reason of the blocked release',
            ),
            renderRow: row => <ReasonTableCell row={row} />,
          },
          {
            width: '10%',
            property: 'createdDate',
            label: t(
              'atlas.labels.date_added',
              'Date added',
              'Label for Date added of the blocked release',
            ),
          },
          {
            width: '10%',
            property: 'dateSubmitted',
            label: t(
              'atlas.labels.date_submitted',
              'Date submitted',
              'Label for Date submitted of the blocked release',
            ),
            helperText: t(
              'atlas.labels.date_submitted.description',
              'The date when you requested approval for a product. Once submitted, products can take up to 5 business days to go live, as stated in the Spotify Service Level Guideline.',
              'Description for date submitted',
            ),
            renderRow: row => <DateSubmittedCell row={row} />,
          },
          {
            width: '10%',
            property: 'albumName',
            label: t(
              'atlas.actions.more_actions',
              'Actions',
              'Label text for more actions',
            ),
            renderRow: row => (
              <ActionTableCell
                row={row}
                mutate={mutate}
                userLicensor={userLicensors.find(
                  licensor => licensor.name === row.licensorName,
                )}
              />
            ),
          },
        ]}
        data={
          (data as AlbumMetadataListResponse)?.albumMetadataListResponse
            ?.albumMetadata || []
        }
      />
      {renderPagination({
        totalResultRows:
          data?.albumMetadataListResponse?.albumMetadata?.length || 0,
        isLastPage: !hasMore,
      })}
    </>
  );
};
